import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CommonTable from "../../../components/Table/Table";
import "./DiscountList.css";
import { Eye, Pencil, Trash2 } from "lucide-react";
import {
  deleteDiscount,
  DiscountListItem,
  getDiscountList,
  updateDiscountStatus,
} from "../../../services/Discount/discount.service";
import { toast } from "react-toastify";

const dateOnly = (value?: string | null) => (value ? value.slice(0, 10) : "-");

interface DiscountRow {
  id: number;
  discountName: string;
  description: string;
  startDate: string;
  endDate: string;
  valueType: "percentage" | "flatAmount";
  entityType: "buyer" | "seller" | "category";
  entityIds: number[];
  entityNames: string[];
  minOrderValue: number | null;
  maxDiscount: number | null;
  isActive: boolean;
}

const DiscountList: React.FC = () => {
  const [search, setSearch] = useState("");
  const [discounts, setDiscounts] = useState<DiscountRow[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewItem, setViewItem] = useState<DiscountRow | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  const loadDiscounts = async () => {
    try {
      const list: DiscountListItem[] = await getDiscountList();
      const mapped: DiscountRow[] = (list || []).map((d) => ({
        id: d.id,
        discountName: d.discountName || "-",
        description: d.description || "-",
        startDate: dateOnly(d.startDate),
        endDate: dateOnly(d.endDate),
        valueType:
          d.valueType === "flat" || d.valueType === "flatAmount"
            ? "flatAmount"
            : "percentage",
        entityType: d.entityType,
        entityIds: d.entityIds || [], // ✅ Ensure array
        entityNames: d.entityNames || [], // ✅ Ensure array
        minOrderValue: d.minOrderValue ?? null,
        maxDiscount: d.maxDiscount ?? null,
        isActive: !!d.isActive,
      }));
      setDiscounts(mapped);
    } catch (error: any) {
      console.error("Failed to load discounts", error);
      toast.error(error?.response?.data?.message || "Failed to load discount list");
      setDiscounts([]);
    }
  };

  useEffect(() => {
    loadDiscounts();
  }, []);

  const handleToggleStatus = async (row: DiscountRow) => {
    if (updatingStatusId === row.id) return;
    const next = !row.isActive;

    try {
      setUpdatingStatusId(row.id);
      const res = await updateDiscountStatus(row.id, next);
      toast.success(res?.message || "Status updated successfully");
      setDiscounts((prev) => prev.map((d) => (d.id === row.id ? { ...d, isActive: next } : d)));
      setViewItem((prev) => (prev && prev.id === row.id ? { ...prev, isActive: next } : prev));
    } catch (error: any) {
      console.error("Status update failed", error);
      toast.error(error?.response?.data?.message || "Failed to update status");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleDelete = async (row: DiscountRow) => {
    if (deletingId === row.id) return;

    try {
      setDeletingId(row.id);
      const res = await deleteDiscount(row.id);
      toast.success(res?.message || "Deleted successfully");
      setDiscounts((prev) => prev.filter((d) => d.id !== row.id));
      setViewItem((prev) => (prev && prev.id === row.id ? null : prev));
    } catch (error: any) {
      console.error("Delete failed", error);
      toast.error(error?.response?.data?.message || "Failed to delete discount");
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = discounts.filter((d) => {
    const term = search.toLowerCase();
    const entityNamesString = (d.entityNames || []).join(" ").toLowerCase(); // ✅ Safe access
    return (
      d.discountName.toLowerCase().includes(term) ||
      d.description.toLowerCase().includes(term) ||
      entityNamesString.includes(term) ||
      d.entityType.toLowerCase().includes(term)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filtered
    .slice(startIndex, startIndex + itemsPerPage)
    .map((row, index) => ({
      ...row,
      serial: startIndex + index + 1,
    }));

  const columns = [
    { key: "serial", label: "S.NO" },
    { key: "discountName", label: "Discount Name" },
    { key: "description", label: "Description" },
    {
      key: "valueType",
      label: "Value Type",
      render: (value: string) => (value === "flatAmount" ? "Flat Amount" : "Percentage"),
    },
    { key: "entityType", label: "Entity Type" },
    {
      key: "minOrderValue",
      label: "Min Order",
      render: (value: number | null) => (value === null ? "-" : value),
    },
    {
      key: "maxDiscount",
      label: "Max Discount",
      render: (value: number | null) => (value === null ? "-" : value),
    },
    { key: "startDate", label: "Start Date" },
    { key: "endDate", label: "End Date" },
    {
      key: "isActive",
      label: "Status",
      render: (value: boolean, row: DiscountRow) => (
        <span
          className="status-badge"
          onClick={() => handleToggleStatus(row)}
          style={{
            cursor: updatingStatusId === row.id ? "not-allowed" : "pointer",
            opacity: updatingStatusId === row.id ? 0.6 : 1,
            backgroundColor: value ? "#d4f5d4" : "#f5d4d4",
            color: value ? "green" : "red",
          }}
        >
          {updatingStatusId === row.id ? "Updating..." : value ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  // ✅ Fixed: Properly pass data to edit page
const handleEdit = (row: DiscountRow) => {
  // Ensure all required fields are present with proper defaults
  const editData = {
    id: row.id,
    discountName: row.discountName,
    name: row.discountName,
    description: row.description,
    startDate: row.startDate,
    endDate: row.endDate,
    valueType: row.valueType,
    type: row.valueType,
    entityType: row.entityType,
    entityIds: row.entityIds || [],
    entityNames: row.entityNames || [],
    minOrderValue: row.minOrderValue ?? null,
    maxDiscount: row.maxDiscount ?? null,
    isActive: row.isActive,
  };
  
  console.log("Passing to edit:", editData); // Debug log
  
  navigate(`/discount/edit/${row.id}`, { 
    state: { discountData: editData } 
  });
};

  return (
    <div className="discount-container">
      <div className="header-bar">
        <div className="left-area">
          <input
            className="search-bar"
            type="text"
            placeholder="Search discount..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
          <span className="hint-text"></span>
        </div>
        <button className="add-btn" onClick={() => navigate("/discount/add")}>
          + Add Discount
        </button>
      </div>

      <CommonTable
        columns={columns}
        data={paginatedData}
        actions={(row: DiscountRow) => (
          <div className="action-icons">
            <button className="icon-btn" title="View" onClick={() => setViewItem(row)}>
              <Eye size={16} />
            </button>
            <button
              className="icon-btn"
              title="Edit"
              onClick={() => handleEdit(row)} // ✅ Use handleEdit function
            >
              <Pencil size={16} />
            </button>
            <button
              className="icon-btn delete"
              title="Delete"
              onClick={() => handleDelete(row)}
              disabled={deletingId === row.id}
              style={{ opacity: deletingId === row.id ? 0.6 : 1 }}
            >
              <Trash2 size={16} color="#ef4444" />
            </button>
          </div>
        )}
      />

      <div className="pagination">
        <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}>
          Previous
        </button>
        <span>
          Page {Math.min(currentPage, totalPages)} of {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage >= totalPages}
        >
          Next
        </button>
      </div>

      {viewItem && (
        <div className="modal-overlay" onClick={() => setViewItem(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Discount Details</h3>
              <button className="close-btn" onClick={() => setViewItem(null)}>
                ×
              </button>
            </div>
            <div className="modal-body detail-body">
              <div className="detail-row"><strong>Name:</strong> {viewItem.discountName}</div>
              <div className="detail-row"><strong>Description:</strong> {viewItem.description}</div>
              <div className="detail-row"><strong>Value Type:</strong> {viewItem.valueType}</div>
              <div className="detail-row"><strong>Entity Type:</strong> {viewItem.entityType}</div>
              <div className="detail-row">
                <strong>Entity Names:</strong> 
                {viewItem.entityNames && viewItem.entityNames.length > 0 
                  ? viewItem.entityNames.join(", ") 
                  : "-"}
              </div>
              <div className="detail-row"><strong>Start Date:</strong> {viewItem.startDate}</div>
              <div className="detail-row"><strong>End Date:</strong> {viewItem.endDate}</div>
              <div className="detail-row"><strong>Min Order:</strong> {viewItem.minOrderValue ?? "-"}</div>
              <div className="detail-row"><strong>Max Discount:</strong> {viewItem.maxDiscount ?? "-"}</div>
              <div className="detail-row"><strong>Status:</strong> {viewItem.isActive ? "Active" : "Inactive"}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountList;